import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * FloatingPanel — a visually detached, inline companion panel that floats over
 * the page (default: the right edge) to surface related content without
 * navigating away. Think "peek at the time reports while staying in the shift
 * planner".
 *
 * How it differs from `components/ui/sheet`:
 *  - Sheet is a MODAL drawer pinned flush to a screen edge (full height, dark
 *    backdrop, locks the page). Use it when the panel should take over.
 *  - FloatingPanel is NON-MODAL by default: a rounded, shadowed card inset from
 *    the edges, no backdrop, and it stays open when you click the page behind
 *    it — so the underlying view (a grid, a form) remains fully interactive.
 *
 * Built on `@radix-ui/react-dialog` so it keeps Escape-to-close, focus return
 * and portalling, but every visual is an explicit Tailwind utility so it renders
 * correctly regardless of the Tailwind preflight state.
 */
const FloatingPanel = ({
	modal = false,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) => (
	<DialogPrimitive.Root modal={modal} {...props} />
);
FloatingPanel.displayName = "FloatingPanel";

const FloatingPanelTrigger = DialogPrimitive.Trigger;
const FloatingPanelClose = DialogPrimitive.Close;
const FloatingPanelPortal = DialogPrimitive.Portal;

const FloatingPanelOverlay = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Overlay
		ref={ref}
		className={cn(
			"fixed inset-0 z-[1100] bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
			className
		)}
		{...props}
	/>
));
FloatingPanelOverlay.displayName = DialogPrimitive.Overlay.displayName;

// The floating card. Inset from the edges (so it reads as "floating" rather
// than a flush drawer), rounded, bordered and shadowed. Slides in from its side
// via tailwindcss-animate.
// EDGE_MARGIN_REM is the base inset from the screen edges — the inset-y-2/
// right-2/left-2 classes below and the `offset` math must stay in sync with it.
const EDGE_MARGIN_REM = 0.5;

const floatingPanelVariants = cva(
	"fixed z-[1100] flex max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-xl border border-border bg-background shadow-2xl outline-none transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-200 data-[state=open]:duration-300",
	{
		variants: {
			side: {
				right:
					"inset-y-2 right-2 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
				left: "inset-y-2 left-2 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
			},
			size: {
				default: "w-[26rem]",
				wide: "w-[38rem]",
			},
		},
		defaultVariants: {
			side: "right",
			size: "default",
		},
	}
);

interface FloatingPanelContentProps
	extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
		VariantProps<typeof floatingPanelVariants> {
	/** Hide the built-in top-right close button. */
	hideCloseButton?: boolean;
	/**
	 * Close the panel when the user clicks/taps outside it. Default `false` — an
	 * inline companion should stay open while you interact with the page behind
	 * it. Set `true` for light-dismiss behaviour.
	 */
	dismissOnOutsideClick?: boolean;
	/** Close on the Escape key. Default `true`. */
	dismissOnEscape?: boolean;
	/**
	 * Distance in `rem` to push the panel inward from its anchored edge, added on
	 * top of the default 0.5rem edge margin. Use it to place several open panels
	 * side by side: give each panel a larger offset than the previous one
	 * (roughly the previous panel's width + a gap). `offset={0}` (default) keeps
	 * the panel flush against the edge margin.
	 */
	offset?: number;
	/** Render a dimming backdrop behind the panel (pair with a modal panel to focus attention). */
	overlay?: boolean;
	/** Extra classes for the optional backdrop. */
	overlayClassName?: string;
	/** Portal mount target. Defaults to `document.body`. */
	container?: HTMLElement | null;
}

const FloatingPanelContent = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Content>,
	FloatingPanelContentProps
>(
	(
		{
			side = "right",
			size,
			className,
			children,
			hideCloseButton,
			dismissOnOutsideClick = false,
			dismissOnEscape = true,
			offset = 0,
			overlay = false,
			overlayClassName,
			container,
			style,
			onInteractOutside,
			onEscapeKeyDown,
			...props
		},
		ref
	) => {
		// Offsetting anchors the panel `offset`rem further from its edge (on top of
		// the EDGE_MARGIN_REM base margin), overriding the right-2/left-2 class so
		// stacked panels don't sit on top of each other. The slide animation
		// defaults to travelling the panel's own width (100%) — enough to clear the
		// screen only when the panel is flush to the edge — so an offset panel also
		// has to travel its inset distance, or it animates in from mid-screen. We
		// extend the tailwindcss-animate enter/exit translate vars to cover both.
		let anchoredStyle = style;
		if (offset) {
			const sign = side === "left" ? -1 : 1;
			const slide = `calc(${sign * 100}% + ${sign * (offset + EDGE_MARGIN_REM)}rem)`;
			anchoredStyle = {
				...style,
				[side === "left" ? "left" : "right"]: `calc(${EDGE_MARGIN_REM}rem + ${offset}rem)`,
				["--tw-enter-translate-x"]: slide,
				["--tw-exit-translate-x"]: slide,
			} as React.CSSProperties;
		}

		return (
			<FloatingPanelPortal container={container ?? undefined}>
				{/* The backdrop BLOCKS the page by covering it and closes the panel
				    on click. Deliberately paired with a NON-modal root: Radix's
				    modal mode pointer-locks everything outside the dialog content,
				    which breaks portaled dropdowns rendered by panel children
				    (e.g. the competence selector). */}
				{overlay && (
					<DialogPrimitive.Close asChild>
						<FloatingPanelOverlay className={overlayClassName} />
					</DialogPrimitive.Close>
				)}
				<DialogPrimitive.Content
					ref={ref}
					className={cn(floatingPanelVariants({ side, size }), className)}
					style={anchoredStyle}
					onInteractOutside={(e) => {
						if (!dismissOnOutsideClick) e.preventDefault();
						onInteractOutside?.(e);
					}}
					onEscapeKeyDown={(e) => {
						if (!dismissOnEscape) e.preventDefault();
						onEscapeKeyDown?.(e);
					}}
					{...props}
				>
					{children}
					{!hideCloseButton && (
						<DialogPrimitive.Close className="absolute right-3 top-3.5 rounded-sm text-gray-500 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
							<X className="h-4 w-4" />
							<span className="sr-only">Stäng</span>
						</DialogPrimitive.Close>
					)}
				</DialogPrimitive.Content>
			</FloatingPanelPortal>
		);
	}
);
FloatingPanelContent.displayName = "FloatingPanelContent";

const FloatingPanelHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			"flex shrink-0 items-start justify-between gap-4 border-b border-border px-5 py-4 pr-10",
			className
		)}
		{...props}
	/>
);
FloatingPanelHeader.displayName = "FloatingPanelHeader";

const FloatingPanelTitle = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Title>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Title
		ref={ref}
		className={cn("text-base font-semibold text-foreground", className)}
		{...props}
	/>
));
FloatingPanelTitle.displayName = DialogPrimitive.Title.displayName;

const FloatingPanelDescription = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Description>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Description
		ref={ref}
		className={cn("text-sm text-muted-foreground", className)}
		{...props}
	/>
));
FloatingPanelDescription.displayName = DialogPrimitive.Description.displayName;

/** Scrollable body — grows to fill the card and scrolls its own overflow. */
const FloatingPanelBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div className={cn("flex-1 overflow-y-auto px-5 py-4", className)} {...props} />
);
FloatingPanelBody.displayName = "FloatingPanelBody";

const FloatingPanelFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			"flex shrink-0 flex-col-reverse gap-2 border-t border-border px-5 py-3 sm:flex-row sm:justify-end",
			className
		)}
		{...props}
	/>
);
FloatingPanelFooter.displayName = "FloatingPanelFooter";

export {
	FloatingPanel,
	FloatingPanelTrigger,
	FloatingPanelClose,
	FloatingPanelPortal,
	FloatingPanelOverlay,
	FloatingPanelContent,
	FloatingPanelHeader,
	FloatingPanelTitle,
	FloatingPanelDescription,
	FloatingPanelBody,
	FloatingPanelFooter,
};
